export type RichTextSize = 'S' | 'M' | 'L';
export type RichTextAlignment = 'left' | 'center' | 'right';

export const RICH_TEXT_LINE_HEIGHT: Record<RichTextSize, number> = {
  S: 1.5,
  M: 1.8,
  L: 1.6,
};

export type RichTextMarkType = 'bold' | 'italic' | 'underline' | 'textSize';

export interface RichTextMark {
  type: RichTextMarkType;
  attrs?: {
    size?: RichTextSize;
  };
}

export interface RichTextTextNode {
  type: 'text';
  text: string;
  marks?: RichTextMark[];
}

export interface RichTextHardBreak {
  type: 'hardBreak';
}

export type RichTextInlineNode = RichTextTextNode | RichTextHardBreak;

export interface RichTextParagraph {
  type: 'paragraph';
  attrs?: {
    textAlign?: RichTextAlignment;
  };
  content?: RichTextInlineNode[];
}

export interface RichTextDoc {
  type: 'doc';
  content: RichTextParagraph[];
}

export interface RichTextRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  sizeS?: boolean;
  sizeL?: boolean;
}

export interface RichTextParagraphRuns {
  runs: RichTextRun[];
  textAlign?: RichTextAlignment;
}

const emptyDoc = (): RichTextDoc => ({
  type: 'doc',
  content: [{ type: 'paragraph' }],
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isRichTextDoc = (value: unknown): value is RichTextDoc => {
  if (!isRecord(value)) return false;
  if (value.type !== 'doc') return false;
  return Array.isArray(value.content);
};

const sanitizeMarks = (marks: unknown): RichTextMark[] | undefined => {
  if (!Array.isArray(marks)) return undefined;
  const next: RichTextMark[] = [];
  const seen = new Set<RichTextMarkType>();
  marks.forEach((mark) => {
    if (!isRecord(mark) || typeof mark.type !== 'string') return;
    if (mark.type === 'bold' && !seen.has('bold')) {
      next.push({ type: 'bold' });
      seen.add('bold');
      return;
    }
    if (mark.type === 'italic' && !seen.has('italic')) {
      next.push({ type: 'italic' });
      seen.add('italic');
      return;
    }
    if (mark.type === 'underline' && !seen.has('underline')) {
      next.push({ type: 'underline' });
      seen.add('underline');
      return;
    }
    if (mark.type === 'textSize' && !seen.has('textSize')) {
      const attrs = isRecord(mark.attrs) ? mark.attrs : {};
      if (attrs.size === 'S' || attrs.size === 'L') {
        next.push({ type: 'textSize', attrs: { size: attrs.size } });
        seen.add('textSize');
      }
    }
  });
  return next.length ? next : undefined;
};

const sanitizeInlineNode = (node: unknown): RichTextInlineNode | null => {
  if (!isRecord(node) || typeof node.type !== 'string') return null;
  if (node.type === 'hardBreak') {
    return { type: 'hardBreak' };
  }
  if (node.type === 'text' && typeof node.text === 'string' && node.text.length > 0) {
    return {
      type: 'text',
      text: node.text,
      marks: sanitizeMarks(node.marks),
    };
  }
  return null;
};

const sanitizeTextAlignment = (value: unknown): RichTextAlignment | undefined => {
  if (value === 'left' || value === 'center' || value === 'right') {
    return value;
  }
  return undefined;
};

const sanitizeParagraph = (node: unknown): RichTextParagraph | null => {
  if (!isRecord(node)) return null;
  if (node.type !== 'paragraph') return null;
  const attrs = isRecord(node.attrs) ? node.attrs : {};
  const textAlign = sanitizeTextAlignment(attrs.textAlign);
  const content = Array.isArray(node.content)
    ? node.content
      .map((child) => sanitizeInlineNode(child))
      .filter((child): child is RichTextInlineNode => child != null)
    : [];
  return {
    type: 'paragraph',
    ...(textAlign ? { attrs: { textAlign } } : {}),
    ...(content.length ? { content } : {}),
  };
};

const sanitizeRichTextDoc = (value: unknown): RichTextDoc => {
  if (!isRichTextDoc(value)) return emptyDoc();
  const content = value.content
    .map((block) => sanitizeParagraph(block))
    .filter((block): block is RichTextParagraph => block != null);

  return content.length ? { type: 'doc', content } : emptyDoc();
};

const plainTextToRichTextDoc = (value: string): RichTextDoc => {
  const raw = value ?? '';
  if (raw === '') return emptyDoc();
  const lines = raw.split(/\r?\n/);
  return {
    type: 'doc',
    content: lines.map((line) => (
      line
        ? { type: 'paragraph', content: [{ type: 'text', text: line }] }
        : { type: 'paragraph' }
    )),
  };
};

export const parseRichText = (value: string | null | undefined): RichTextDoc => {
  const raw = value ?? '';
  const trimmed = raw.trim();
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(raw);
      if (isRichTextDoc(parsed)) {
        return sanitizeRichTextDoc(parsed);
      }
    } catch {
      // fall through to plain text
    }
  }
  return plainTextToRichTextDoc(raw);
};

const richTextDocToPlainText = (doc: RichTextDoc): string => {
  const lines = doc.content.map((paragraph) => (
    (paragraph.content ?? []).map((node) => {
      if (node.type === 'text') return node.text;
      if (node.type === 'hardBreak') return '\n';
      return '';
    }).join('')
  ));
  const plain = lines.join('\n');
  return plain === '' ? '' : plain;
};

export const richTextToPlainText = (value: string | null | undefined): string => (
  richTextDocToPlainText(parseRichText(value))
);

const richTextDocHasFormatting = (doc: RichTextDoc): boolean => (
  doc.content.some((paragraph) =>
    !!paragraph.attrs?.textAlign || (paragraph.content ?? []).some((node) => (
      node.type === 'text' && !!node.marks && node.marks.length > 0
    )))
);

const isEffectivelyEmptyDoc = (doc: RichTextDoc): boolean => (
  doc.content.length === 1 && !(doc.content[0].content && doc.content[0].content.length)
);

const serializeRichTextDoc = (doc: RichTextDoc): string => {
  const sanitized = sanitizeRichTextDoc(doc);
  if (isEffectivelyEmptyDoc(sanitized)) return '';
  if (!richTextDocHasFormatting(sanitized)) {
    return richTextDocToPlainText(sanitized);
  }
  return JSON.stringify(sanitized);
};

export const serializeEditorContent = (value: unknown): string => (
  serializeRichTextDoc(sanitizeRichTextDoc(value))
);

export interface RichTextFormatting {
  textAlign?: RichTextAlignment;
  italic?: boolean;
  size?: RichTextSize;
}

export const applyRichTextFormatting = (
  value: string | null | undefined,
  formatting: RichTextFormatting,
): string => {
  const doc = parseRichText(value);
  const content = doc.content.map((paragraph) => ({
    ...paragraph,
    ...(formatting.textAlign
      ? { attrs: { ...paragraph.attrs, textAlign: formatting.textAlign } }
      : {}),
    ...(paragraph.content
      ? {
        content: paragraph.content.map((node) => {
          if (node.type !== 'text') return node;
          let marks = [...(node.marks ?? [])];

          if (formatting.italic !== undefined) {
            marks = marks.filter((mark) => mark.type !== 'italic');
            if (formatting.italic) marks.push({ type: 'italic' });
          }

          if (formatting.size !== undefined) {
            marks = marks.filter((mark) => mark.type !== 'textSize');
            if (formatting.size !== 'M') {
              marks.push({ type: 'textSize', attrs: { size: formatting.size } });
            }
          }

          return {
            ...node,
            ...(marks.length ? { marks } : { marks: undefined }),
          };
        }),
      }
      : {}),
  }));

  return serializeRichTextDoc({ type: 'doc', content });
};

export const richTextToParagraphs = (
  value: string | null | undefined,
): RichTextParagraphRuns[] => {
  const doc = parseRichText(value);
  return doc.content.map((paragraph) => {
    const runs: RichTextRun[] = [];
    (paragraph.content ?? []).forEach((node) => {
      if (node.type === 'hardBreak') {
        runs.push({ text: '\n' });
        return;
      }
      const marks = node.marks ?? [];
      runs.push({
        text: node.text,
        bold: marks.some((mark) => mark.type === 'bold'),
        italic: marks.some((mark) => mark.type === 'italic'),
        underline: marks.some((mark) => mark.type === 'underline'),
        sizeS: marks.some((mark) => mark.type === 'textSize' && mark.attrs?.size === 'S'),
        sizeL: marks.some((mark) => mark.type === 'textSize' && mark.attrs?.size === 'L'),
      });
    });
    return {
      runs,
      textAlign: paragraph.attrs?.textAlign,
    };
  });
};

export const richTextToRuns = (value: string | null | undefined): RichTextRun[][] => (
  richTextToParagraphs(value).map((paragraph) => paragraph.runs)
);
